import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Slider,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from "@mui/material";
import {
  DisplaySettings as DisplaySettingsIcon,
  Tune as BehaviorIcon,
  TableChart as TableChartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  GridFilterPanel,
  GridSortModel,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";

type GridSortItem = GridSortModel[number];
import {
  DataGridSettings,
  DEFAULT_SETTINGS,
  useDataGridSettings,
  loadDefaultFilterModel,
  saveDefaultFilterModel,
  clearDefaultFilterModel,
  loadDefaultSortModel,
  saveDefaultSortModel,
  clearDefaultSortModel,
  isSortableDataColumn,
  sortModelToLabel,
} from "./DataGridSettings";

interface DataGridSettingsModalProps {
  open: boolean;
  onClose: () => void;
  tableName: string;
}

type SettingsPage = "display" | "behavior" | "table";

const NAV_ITEMS: { id: SettingsPage; label: string; icon: React.ReactNode }[] =
  [
    {
      id: "display",
      label: "Display",
      icon: <DisplaySettingsIcon fontSize="small" />,
    },
    {
      id: "behavior",
      label: "Behavior",
      icon: <BehaviorIcon fontSize="small" />,
    },
    {
      id: "table",
      label: "Table Options",
      icon: <TableChartIcon fontSize="small" />,
    },
  ];

const ODD_ROW_PRESETS = [
  { label: "Blue-grey", color: "#e8edf5" },
  { label: "Sage", color: "#e8f0ea" },
  { label: "Lavender", color: "#ede8f5" },
  { label: "Peach", color: "#f5ede8" },
  { label: "Grey", color: "#f0f0f0" },
];

function DisplayPage({
  draft,
  setDraft,
}: {
  draft: DataGridSettings;
  setDraft: (d: DataGridSettings) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Font size */}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Font size
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {draft.fontSize}px
          </Typography>
        </Box>
        <Slider
          value={draft.fontSize}
          min={10}
          max={18}
          step={1}
          size="small"
          marks={[
            { value: 10, label: "10px" },
            { value: 13, label: "default" },
            { value: 18, label: "18px" },
          ]}
          onChange={(_e, value) =>
            setDraft({ ...draft, fontSize: value as number })
          }
        />
      </Box>

      <Divider />

      {/* Compression */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Compression
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Cell padding
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {draft.cellPadding}px
            </Typography>
          </Box>
          <Slider
            value={draft.cellPadding}
            min={2}
            max={16}
            step={1}
            size="small"
            marks={[
              { value: 2, label: "2px" },
              { value: 16, label: "default" },
            ]}
            onChange={(_e, value) =>
              setDraft({ ...draft, cellPadding: value as number })
            }
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={draft.stackColumnHeaders}
              onChange={(e) =>
                setDraft({ ...draft, stackColumnHeaders: e.target.checked })
              }
            />
          }
          label="Stack column header words"
        />
      </Box>

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={draft.alternatingRows}
            onChange={(e) =>
              setDraft({ ...draft, alternatingRows: e.target.checked })
            }
          />
        }
        label="Alternating row colors"
      />

      {draft.alternatingRows && (
        <>
          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Alternating row color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <input
                type="color"
                value={draft.oddRowColor}
                onChange={(e) =>
                  setDraft({ ...draft, oddRowColor: e.target.value })
                }
                style={{
                  width: 36,
                  height: 28,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 2,
                  background: "none",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {draft.oddRowColor}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", gap: 0.75, mt: 1.25, flexWrap: "wrap" }}
            >
              {ODD_ROW_PRESETS.map((preset) => (
                <Tooltip key={preset.label} title={preset.label}>
                  <Box
                    onClick={() =>
                      setDraft({ ...draft, oddRowColor: preset.color })
                    }
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 0.75,
                      bgcolor: preset.color,
                      border:
                        draft.oddRowColor === preset.color
                          ? "2px solid #1976d2"
                          : "1px solid #ccc",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                      "&:hover": { borderColor: "#1976d2" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Base row color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <input
                type="color"
                value={draft.evenRowColor}
                onChange={(e) =>
                  setDraft({ ...draft, evenRowColor: e.target.value })
                }
                style={{
                  width: 36,
                  height: 28,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 2,
                  background: "none",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {draft.evenRowColor}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    height: 32,
                    bgcolor:
                      i % 2 === 0 ? draft.oddRowColor : draft.evenRowColor,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    borderBottom: i < 3 ? "1px solid rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  Row {i + 1}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

function BehaviorPage({
  draft,
  setDraft,
}: {
  draft: DataGridSettings;
  setDraft: (d: DataGridSettings) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={draft.preserveSelectionOnSearch}
            onChange={(e) =>
              setDraft({ ...draft, preserveSelectionOnSearch: e.target.checked })
            }
          />
        }
        label="Preserve row selection across searches"
      />

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Actions column position
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose whether the edit/delete actions column appears at the front or
          back of the table.
        </Typography>
        <RadioGroup
          value={draft.actionsColumnPosition}
          onChange={(e) =>
            setDraft({
              ...draft,
              actionsColumnPosition: e.target.value as "front" | "back",
            })
          }
        >
          <FormControlLabel
            value="back"
            control={<Radio size="small" />}
            label="Back (default)"
          />
          <FormControlLabel
            value="front"
            control={<Radio size="small" />}
            label="Front"
          />
        </RadioGroup>
      </Box>
    </Box>
  );
}

function DefaultSortSection({ tableName }: { tableName: string }) {
  const apiRef = useGridApiContext();
  const [savedSort, setSavedSort] = useState<GridSortModel | null>(() =>
    loadDefaultSortModel(tableName)
  );
  // Live sort model of the grid: the rows below both display and edit it,
  // like the filter panel above does for filters.
  const sortDraft = useGridSelector(apiRef, gridSortModelSelector);
  const setSortDraft = (
    update: GridSortModel | ((prev: GridSortModel) => GridSortModel)
  ) =>
    apiRef.current.setSortModel(
      typeof update === "function" ? update(sortDraft) : update
    );

  const sortableColumns = apiRef.current
    .getAllColumns()
    .filter(isSortableDataColumn);
  const allColumns = apiRef.current.getAllColumns();

  const updateRow = (index: number, patch: Partial<GridSortItem>) =>
    setSortDraft((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );

  const handleAddRow = () => {
    const used = new Set(sortDraft.map((s) => s.field));
    const next = sortableColumns.find((c) => !used.has(c.field));
    if (!next) return;
    setSortDraft((prev) => [...prev, { field: next.field, sort: "asc" }]);
  };

  const handleSave = () => {
    saveDefaultSortModel(tableName, sortDraft);
    setSavedSort(sortDraft);
  };

  const handleClear = () => {
    clearDefaultSortModel(tableName);
    setSavedSort(null);
    setSortDraft([]);
  };

  const hasSaved = !!savedSort && savedSort.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Default Sort
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The sort applied each time the <strong>{tableName}</strong> table
          loads (a sort in the page URL always takes priority).{" "}
          {hasSaved
            ? `Currently saved: ${sortModelToLabel(savedSort!, allColumns)}.`
            : "No default sort is currently saved."}
        </Typography>
      </Box>

      {sortDraft.map((item, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Select
            size="small"
            value={item.field}
            onChange={(e) => updateRow(index, { field: e.target.value })}
            sx={{ minWidth: 220 }}
          >
            {allColumns
              .filter(
                (c) =>
                  c.field === item.field ||
                  (isSortableDataColumn(c) &&
                    !sortDraft.some((s) => s.field === c.field))
              )
              .map((c) => (
                <MenuItem key={c.field} value={c.field}>
                  {c.headerName ?? c.field}
                </MenuItem>
              ))}
          </Select>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={item.sort}
            onChange={(_, value) => value && updateRow(index, { sort: value })}
          >
            <ToggleButton value="asc">Asc</ToggleButton>
            <ToggleButton value="desc">Desc</ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            size="small"
            aria-label="Remove sort column"
            onClick={() =>
              setSortDraft((prev) => prev.filter((_, i) => i !== index))
            }
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          disabled={sortDraft.length >= sortableColumns.length}
        >
          Add sort column
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {sortDraft.length > 0
          ? `Current sort: ${sortModelToLabel(sortDraft, allColumns)}`
          : "No sort selected."}
      </Typography>

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          onClick={handleClear}
          color="warning"
          variant="outlined"
          size="small"
          disabled={!hasSaved}
        >
          Clear Default Sort
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={sortDraft.length === 0}
        >
          Save as Default Sort
        </Button>
      </Box>
    </Box>
  );
}

function TableOptionsPage({ tableName }: { tableName: string }) {
  const apiRef = useGridApiContext();
  const [hasSavedDefaults, setHasSavedDefaults] = useState(false);

  useEffect(() => {
    const saved = loadDefaultFilterModel(tableName);
    setHasSavedDefaults(!!saved);
    if (saved) {
      apiRef.current.setFilterModel(saved);
    }
  }, [tableName]);

  const handleSave = () => {
    const currentModel = apiRef.current.state.filter.filterModel;
    saveDefaultFilterModel(tableName, currentModel);
    setHasSavedDefaults(true);
  };

  const handleClear = () => {
    clearDefaultFilterModel(tableName);
    apiRef.current.setFilterModel({ items: [] });
    setHasSavedDefaults(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Default Filters
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Filters configured below are applied automatically each time the{" "}
          <strong>{tableName}</strong> table loads.{" "}
          {hasSavedDefaults
            ? "You currently have saved default filters."
            : "No default filters are currently saved."}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ "& .MuiDataGrid-panelContent": { pt: 2.5 } }}>
        <GridFilterPanel />
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          onClick={handleClear}
          color="warning"
          variant="outlined"
          size="small"
          disabled={!hasSavedDefaults}
        >
          Clear Default Filters
        </Button>
        <Button onClick={handleSave} variant="contained" size="small">
          Save as Default Filters
        </Button>
      </Box>
      <Divider />
      <DefaultSortSection tableName={tableName} />
    </Box>
  );
}

export function DataGridSettingsModal({
  open,
  onClose,
  tableName,
}: DataGridSettingsModalProps) {
  const { settings, updateSettings } = useDataGridSettings();
  const [draft, setDraft] = useState<DataGridSettings>(settings);
  const [activePage, setActivePage] = useState<SettingsPage>("display");

  const handleEntered = () => setDraft({ ...settings });

  const needsReload =
    draft.stackColumnHeaders !== settings.stackColumnHeaders ||
    draft.cellPadding !== settings.cellPadding;

  const handleSave = () => {
    updateSettings(draft);
    if (needsReload) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const handleReset = () => setDraft({ ...DEFAULT_SETTINGS });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ transition: { onEntered: handleEntered } }}
    >
      <DialogTitle sx={{ pb: 0 }}>DataGrid Global Options</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", minHeight: 360 }}>
          {/* Left nav */}
          <Box
            sx={{
              width: 160,
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              pt: 1,
            }}
          >
            <List dense disablePadding>
              {NAV_ITEMS.map((item) => (
                <ListItemButton
                  key={item.id}
                  selected={activePage === item.id}
                  onClick={() => setActivePage(item.id)}
                  sx={{
                    gap: 1,
                    "&.Mui-selected": {
                      bgcolor: "action.selected",
                      borderRight: "2px solid",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {item.icon}
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Right content */}
          <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
            {activePage === "display" && (
              <DisplayPage draft={draft} setDraft={setDraft} />
            )}
            {activePage === "behavior" && (
              <BehaviorPage draft={draft} setDraft={setDraft} />
            )}
            {activePage === "table" && (
              <TableOptionsPage tableName={tableName} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        {activePage !== "table" && (
          <Button onClick={handleReset} color="inherit" size="small">
            Reset to Defaults
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined" size="small">
          {activePage === "table" ? "Close" : "Cancel"}
        </Button>
        {activePage !== "table" && (
          <Button onClick={handleSave} variant="contained" size="small">
            {needsReload ? "Save & Reload Page" : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
